import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'
import withStyles from 'react-jss'
import classNames from 'classnames'
import { Progress } from 'reactstrap'

import { CommunityWelcomeModal } from 'shared/components/community/welcome-modal/CommunityWelcomeModal'
import CommunityTreeItem from 'shared/components/community/components/lesson-tree-community-item/CommunityTreeItem'
import { SellerWarningModal } from 'shared/components/community/seller-warning-modal/SellerWarningModal'
import { EloArrowElbowDownRight } from '@elo-ui/components/icons/regular'
import { Ellipsis } from '@elo-kit/components/ellipsis/Ellipsis'

import { SIDEBAR_POSITIONS } from 'constants/themes.constants'
import { ELOPAGE_PROFILE_CABINETS, ELOPAGE_CABINETS_URL, ROOT_URL } from 'constants/general.constants'
import { PROFILES } from 'constants/profile.constants'
import { COMMUNITY_MEMBERS_ROLES } from 'constants/communities.constants'

import { isEdge, isInternetExplorer } from 'utils/browsers.utils'
import { setCookies } from 'libs/common/cookies'

import sideNavigationStyles from './sideNavigationStyles'
import LessonStatus from './LessonStatus'

const MenuContainer = withStyles(sideNavigationStyles)((props) => {
  const { classes, children, lessonMenuFixed, footerIsShown } = props
  const containerClasses = classNames(
    'menu-wrapper',
    lessonMenuFixed ? classes.menuContainerFixed : classes.menuContainer,
    footerIsShown && classes.menuContainerBottomCutted
  )
  return (
    <div className={classes.sideNavContainer}>
      <div className={containerClasses} id='lessons-menu-list'>
        <div className='header' />
        {children}
      </div>
    </div>
  )
})

const Progressbar = withStyles(sideNavigationStyles)((props) => {
  const { classes, title, progress, showProgress, toggleSideMobileMenu } = props

  return (
    <div className={`progress-container ${classes.progressContainer}`}>
      <div className='mobile-close'>
        <h5 className='course-name'>{title}</h5>
        <i className='fas fa-times-circle' onClick={toggleSideMobileMenu} />
      </div>
      {showProgress && (
        <Fragment>
          <div className='progress-title'>
            <div className='completed float-left'>{I18n.t('react.payer.common.yourProgress')}</div>
            <div className='percents float-right'>{progress}%</div>
          </div>
          <Progress value={progress} color='success' />
        </Fragment>
      )}
    </div>
  )
})

@observer
class CustomSideNav extends Component {
  @observable lessonMenuFixed = false
  @observable footerIsShown = false
  @observable lessonsMenuOffset = 0
  @observable communityModalOpened = this.props.courseViewsStore.communityModalIsOpenedByDefault

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    const lessonsMenu = document.getElementById('lessons-menu-list')
    this.lessonsMenuOffset = lessonsMenu.offsetTop
    window.addEventListener('scroll', this.handleLessonMenuPosition, true)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleLessonMenuPosition, true)
  }

  @action handleLessonMenuPosition = () => {
    const { scrollHeight } = document.documentElement
    const footer = document.getElementById('footer-block')
    const footerShown = footer ? scrollHeight - (window.scrollY + window.innerHeight - 30) < footer.clientHeight : false

    if (window.scrollY > this.lessonsMenuOffset) {
      if (!this.lessonMenuFixed) {
        this.lessonMenuFixed = true
      }
    } else {
      if (this.lessonMenuFixed) {
        this.lessonMenuFixed = false
      }
    }

    if (footerShown) {
      if (!this.footerIsShown) {
        this.footerIsShown = true
      }
    } else {
      if (this.footerIsShown) {
        this.footerIsShown = false
      }
    }
  }

  @action toggleCommunityModal = () => {
    this.communityModalOpened = !this.communityModalOpened
  }

  @action openAsSeller = () => {
    const { id: communityId } = this.props?.community
    setCookies('cabinet_profile_type', ELOPAGE_PROFILE_CABINETS[PROFILES.seller]).then(
      () =>
        (window.location.href = `${ROOT_URL}/${ELOPAGE_CABINETS_URL[PROFILES.seller]}/communities/${communityId}/feed`)
    )

    this.communityModalOpened = !this.communityModalOpened
  }

  getMenuItem = (lesson) => (lesson.isCategory ? this.getCategory(lesson) : this.getLesson(lesson))

  getCategory = (lesson) => {
    const { isCategoryOpened, toggleMenu } = this.props
    const isOpened = isCategoryOpened(lesson.id)
    const categoryIconClasses = classNames('caret fa', {
      'fa-caret-down': isOpened,
      'fa-caret-right': !isOpened,
    })
    return (
      <Fragment>
        {lesson.active && (
          <div className='category-wrap'>
            <div className='category-head' onClick={() => toggleMenu(lesson.id)}>
              <i className={categoryIconClasses} />
              <span className='category-name'>{lesson.name}</span>
            </div>

            {isOpened && (
              <div className='category-lessons'>
                <div className='category-lessons-overlay' />
                {lesson.children.map((lesson) => (
                  <Fragment key={lesson.id}>{this.getMenuItem(lesson)}</Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </Fragment>
    )
  }

  getLesson = (lesson) => {
    const {
      courseViewsStore,
      courseViewsStore: {
        selectLesson,
        activeLesson,
        viewConfig: { isShop, isPayerCabinet },
        courseSession,
      },
    } = this.props

    const locked = !lesson.preview
    const shouldCompletePrevQuizzes = isPayerCabinet && courseViewsStore.hasRequiredPrevQuizzes(lesson.id)
    const isActive = String(activeLesson.id) === String(lesson.id)

    const isQuizVisible = isActive && lesson.quizEnabled && !lesson.static && !courseSession.overByTime

    const lessonItemClasses = classNames('lesson-wrap', {
      active: isActive,
      locked: isShop && locked,
      'require-quiz-finish': shouldCompletePrevQuizzes,
    })

    const customScroll = () => {
      const lessonsList = document.getElementById('lessons-menu-list')
      if (this.lessonsMenuOffset && this.lessonMenuFixed && !(isEdge || isInternetExplorer)) {
        window.scrollTo(0, this.lessonsMenuOffset + 10)
        lessonsList && lessonsList.scrollTo && lessonsList.scrollTo(0, lessonsList.scrollTop)
      }
    }

    return (
      <Fragment>
        {lesson.active && (
          <>
            <div
              className={lessonItemClasses}
              onClick={() => {
                selectLesson(lesson.id, () => {})
                customScroll()
              }}
            >
              <div className='lesson-name'>{lesson.name}</div>
              {isShop && locked && <i className='fas fa-lock' />}
              {isPayerCabinet && <LessonStatus lesson={lesson} courseViewsStore={courseViewsStore} />}
            </div>
            {isQuizVisible && (
              <div className='quiz-item quiz-item--relative'>
                <EloArrowElbowDownRight />
                <Ellipsis>{I18n.t('shared.common.quiz')}</Ellipsis>
              </div>
            )}
          </>
        )}
      </Fragment>
    )
  }

  render() {
    const {
      communityMemberStore,
      containerClasses,
      sideBarSide,
      courseViewsStore,
      classes,
      community,
      handleCloseSidebar,
    } = this.props
    const { item: member } = communityMemberStore
    const {
      courseSession: { progress } = {},
      product: { name: productName } = {},
      lessons,
      viewConfig: { isPayerCabinet } = {},
      toggleSideMobileMenu,
      activeCommunity,
      setActiveCommunity,
    } = courseViewsStore || {}

    const courseSideNavClasses = classNames(
      containerClasses,
      sideBarSide === SIDEBAR_POSITIONS.left && classes.leftSideNav,
      sideBarSide === SIDEBAR_POSITIONS.right && classes.rightSideNav,
      sideBarSide === SIDEBAR_POSITIONS.right && this.lessonMenuFixed && classes.rightFixedSideNav
    )
    const isCommunityOwner = member && member.role === COMMUNITY_MEMBERS_ROLES.owner

    return (
      <div className={courseSideNavClasses}>
        <MenuContainer lessonMenuFixed={this.lessonMenuFixed} footerIsShown={this.footerIsShown}>
          <Progressbar
            showProgress={isPayerCabinet}
            title={productName}
            progress={progress}
            toggleSideMobileMenu={toggleSideMobileMenu}
          />

          <div className='menu-lessons-tree'>
            <CommunityTreeItem
              community={community}
              activeCommunity={activeCommunity}
              setActiveCommunity={(community) => {
                setActiveCommunity(community)
                handleCloseSidebar()
              }}
              toggleCommunityModal={this.toggleCommunityModal}
              memberDiscussionId={member?.discussionId}
              isCommunityOwner={isCommunityOwner}
            />
            {lessons.map((lesson) => (
              <Fragment key={`lesson-${lesson.id}`}>{this.getMenuItem(lesson)}</Fragment>
            ))}
          </div>

          {this.communityModalOpened && (
            <CommunityWelcomeModal
              isOpen
              community={community}
              toggle={this.toggleCommunityModal}
              isCommunityOwner={isCommunityOwner}
            >
              {isCommunityOwner && (
                <SellerWarningModal toggle={this.toggleCommunityModal} openAsSeller={this.openAsSeller} />
              )}
            </CommunityWelcomeModal>
          )}
        </MenuContainer>
      </div>
    )
  }
}

export default withStyles(sideNavigationStyles)(CustomSideNav)
